import type { ComputedRef } from "vue";

type StoredValue<T> = {
    subscribers: ComputedRef<number>;
    unsubscribe: () => void;
    value: ComputedRef<T>;
    pending: ComputedRef<boolean>;
};

export const useValueStore = defineStore("value-store", () => {
    const values = ref<
        Record<
            string,
            {
                value: any;
                subscribers: number;
            }
        >
    >({});

    const pending = ref<string[]>([]);

    function useStoredValue<T>(
        key: string,
        callback?: () => T,
    ): StoredValue<T> {
        if (!values.value) {
            values.value = {};
        }

        if (!values.value[key]) {
            values.value[key] = {
                value: callback ? callback() : undefined,
                subscribers: 1,
            };
        } else {
            values.value[key].subscribers += 1;
        }

        return {
            pending: computed(() => pending.value.includes(key)),
            value: computed(() => {
                if (values.value && values.value[key]) {
                    return values.value[key].value;
                }

                return undefined;
            }) as ComputedRef<T>,
            subscribers: computed(() => {
                if (values.value && values.value[key]) {
                    return values.value[key].subscribers;
                }

                return 0;
            }) as ComputedRef<number>,
            unsubscribe: () => {
                if (values.value && values.value[key]) {
                    values.value[key].subscribers -= 1;

                    if (values.value[key].subscribers === 0) {
                        delete values.value[key];
                    }
                }
            },
        };
    }

    function useUpdateStoredValue<T>(
        key: string,
        callback: (() => T) | T,
    ): void {
        if (!values.value[key]) {
            return;
        } else {
            values.value[key].value =
                typeof callback === "function"
                    ? (callback as () => T)()
                    : callback;
        }
    }

    async function useAsyncStoredValue<T>(
        key: string,
        value: () => Promise<T>,
    ): Promise<StoredValue<T>> {
        if (!values.value[key] && !pending.value.includes(key)) {
            pending.value = [key, ...pending.value];
            values.value[key] = {
                value: await value(),
                subscribers: 1,
            };

            pending.value = pending.value.filter(
                (pendingKey) => pendingKey != key,
            );
        }

        return useStoredValue(key);
    }

    return {
        values,
        pending,
        useUpdateStoredValue,
        useStoredValue,
        useAsyncStoredValue,
    };
});

export const useStoredValue = <T>(
    key: string,
    value: () => T,
): StoredValue<T> => {
    const { useStoredValue } = useValueStore();

    return useStoredValue(key, value);
};
export const useUpdateStoredValue = <T>(key: string, value: (() => T) | T) => {
    const { useUpdateStoredValue } = useValueStore();

    useUpdateStoredValue(key, value);
};

export const useAsyncStoredValue = async <T>(
    key: string,
    value: () => Promise<T>,
): Promise<StoredValue<T>> => {
    const { useAsyncStoredValue } = useValueStore();

    return useAsyncStoredValue(key, value);
};
